import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react'
import React, { use, useCallback, useDeferredValue, useEffect, useState } from 'react'
import md5 from 'blueimp-md5'
import { match } from 'path-to-regexp'

import { sleep } from '@app/utils/sleep'
import { withForceAbort } from '@app/utils/withForceAbort'

import { resultError, resultOk } from '@app/packages/Result/Result'

import { useOnChange } from '@app/hooks/useOnChange'

import { BrowserHistory } from './BrowserHistory'
import { Context } from './Context'
import { createRedirectLocation } from './createRedirectLocation'
import { NotFoundError } from './NotFoundError'
import { RedirectError } from './RedirectError'
import { RouteContextProvider } from './RouteContext'
import { RouterContextProvider } from './RouterContext'
import { RouterOutletProvider } from './RouterOutlet'
import type {
  History,
  Location,
  NavigationAction,
  RouteDescriptorRedirect,
  RouterContextInterface,
  RouterInterfaceAfterRenderCallback,
  RouterRouteDescriptor,
} from './types'
import { locationToString } from './utils'

export type BrowserRouterProps = PropsWithChildren<{
  routes: RouterRouteDescriptor[]
  afterRender?: RouterInterfaceAfterRenderCallback
  onSuspend?: <T>(promise: Promise<T>) => Promise<T>
}>

export const createBrowserRouter = ({
  history,
  context = new Context(),
  onRouterReady,
  onNavigation = (_location, _action, promise) => promise,
  shouldHardNavigate,
}: {
  history?: History
  context?: Context
  onRouterReady?: (router: RouterContextInterface) => void
  onNavigation?: (location: Location, action: NavigationAction, promise: Promise<void>) => Promise<void>
  /** Asked before the router commits to a location: return true to re-request it from the server instead. */
  shouldHardNavigate?: () => boolean
}) => {
  const bundleResolver = new Resolver()
  const routesResolver = new Resolver()
  const fetchResolver = new Resolver()

  history = history ?? new BrowserHistory()

  const getBundle = async (location: Location, routes: RouterRouteDescriptor[]) => {
    const currentRoute = await (async () => {
      for (const route of routes) {
        const path = typeof route.path === 'function' ? await route.path(location, context) : route.path
        if (!path) continue
        const matched = match(path)(location.pathname)
        if (matched) {
          return {
            route,
            params: (matched.params ?? {}) as Record<string, string>,
          }
        }
      }
      return null
    })()

    if (!currentRoute) throw NotFoundError.create(location)

    if (isRedirect(currentRoute.route)) {
      const loc = createRedirectLocation(currentRoute.route, location)
      throw RedirectError.create(loc, false)
    }

    const bundle = (await currentRoute.route.component(location)).routeBundle
    return {
      bundle,
      currentRoute: {
        route: currentRoute.route,
        params: currentRoute.params,
      },
    }
  }

  let initial = true

  const defaultOnSuspend = <T,>(p: Promise<T>) => p

  const BrowserRouter: FunctionComponent<BrowserRouterProps> = ({ routes, afterRender, onSuspend = defaultOnSuspend, children }) => {
    const [[location, action], setLocation] = useState(() => [history.location, history.action] as const)

    const handleNavigation = useCallback(
      (p: Promise<void>) =>
        onNavigation(
          location,
          action,
          p.catch(() => {})
        ),
      [location, action]
    )

    const routesHash = md5(routes.map(r => r.eventName).join('|'))
    routes = use(routesResolver.resolve(routesHash, async () => routes))

    const renderDataResult = use(
      useDeferredValue(
        bundleResolver.resolve([routesHash, location.key ?? 'initial'].join(':'), async signal => {
          try {
            const result = resultOk({
              location,
              action,
              data: await wrapPromise(
                handleNavigation,
                async () =>
                  await (initial ? defaultOnSuspend : onSuspend)(
                    withForceAbort(
                      signal,
                      getBundle(location, routes).then(bundle => {
                        return fetchResolver.resolve([location.key ?? 'initial', bundle.currentRoute.route.eventName].join(':'), async signal => {
                          const router: RouterContextInterface = {
                            history,
                            location,
                            action,
                            context,
                          }
                          onRouterReady?.(router)
                          if (bundle.bundle.fetchData && (location.state?.hard ?? true)) {
                            await bundle.bundle.fetchData({
                              context,
                              action,
                              history,
                              location,
                              route: bundle.currentRoute.route,
                              params: bundle.currentRoute.params,
                              abortSignal: signal,
                              state: { isServer: false, isBrowser: true, isBrowserInitial: initial },
                            })
                          }
                          initial = false
                          return bundle
                        })
                      })
                    )
                  )
              ),
            })

            void afterRender?.({
              context,
              history,
              location: result.value.location,
              action: result.value.action,
              matchedRoute: result.value.data.currentRoute,
            })

            return result
          } catch (e) {
            return resultError(e)
          }
        })
      )
    )

    if (renderDataResult.error) {
      const e = renderDataResult.value
      if (e instanceof RedirectError) {
        void sleep(10).then(() => {
          history.replace(e.location, e.location.state)
        })
        throw sleep(1000)
      }
      throw e
    }
    const renderData = renderDataResult.value

    useEffect(() => {
      const unlisten = history.listen((location, action) => {
        // Gate before setLocation: once state is committed the bundle and its fetchData are already
        // in flight, and a hard navigation would only race them. A `hard: false` location opted out
        // of full navigation processing, so it cannot ask for a full page load either.
        if (location.state?.hard !== false && shouldHardNavigate?.()) {
          window.location.href = locationToString(location)
          return
        }
        setLocation([location, action])
      })

      return () => {
        unlisten()
      }
    }, [routes])

    const Content = useCallback(
      ({ rdata }: { rdata: typeof renderData }) => {
        const passed = {
          route: rdata.data.currentRoute.route,
          location: rdata.location,
          params: rdata.data.currentRoute.params,
        }
        const ctx = 'useRouteCtx' in rdata.data.bundle ? rdata.data.bundle.useRouteCtx(passed) : null
        const layout = (children: ReactNode) => rdata.data.bundle.getLayout({ ...passed, ctx, children })
        const Content = rdata.data.bundle.component
        const content = <Content ctx={ctx} location={rdata.location} params={rdata.data.currentRoute.params} route={rdata.data.currentRoute.route} />

        return layout(content)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [renderData.data.bundle['useRouteCtx']]
    )

    useOnChange(() => {
      if (renderData.location.state?.scroll === 'save') {
        // pass
      } else if (Array.isArray(renderData.location.state?.scroll)) {
        const [x, y] = renderData.location.state.scroll
        window.scrollTo(x, y)
      } else {
        window.scrollTo(0, 0)
      }
    }, [renderData])

    return (
      <RouterContextProvider action={renderData.action} context={context} history={history} location={renderData.location}>
        <RouteContextProvider val={renderData.data.currentRoute}>
          {!children ? <Content rdata={renderData} /> : <RouterOutletProvider value={<Content rdata={renderData} />}>{children}</RouterOutletProvider>}
        </RouteContextProvider>
      </RouterContextProvider>
    )
  }

  return BrowserRouter
}

const isRedirect = (obj: RouterRouteDescriptor): obj is RouteDescriptorRedirect => {
  return 'redirect' in obj && !!obj.redirect
}

/** Inserts intermediate handling for a promise */
const wrapPromise = <T,>(handler: (p: Promise<void>) => Promise<void>, cb: () => Promise<T>) => {
  const p = cb()
  return handler(p.then(() => {})).then(() => p)
}

class Resolver {
  private cache = new SizedSet<{ abortController: AbortController; promise: Promise<any> }>(2)

  resolve = <T,>(key: string, promise: (signal: AbortSignal) => Promise<T>) => {
    const existing = this.cache.get(key)
    if (existing) return existing.promise as Promise<T>
    const ab = new AbortController()
    const p = promise(ab.signal)
    this.cache.add(key, { abortController: ab, promise: p })
    return p
  }
}

class SizedSet<T> {
  private maxSize: number
  private set: [string, T][] = []

  constructor(maxSize: number) {
    this.maxSize = maxSize
  }

  get = (key: string): T | undefined => {
    const found = this.set.find(([k]) => k === key)
    return found ? found[1] : undefined
  }

  add = (key: string, val: T) => {
    this.set = this.set.filter(([k]) => k !== key)
    this.set.push([key, val])
    if (this.set.length > this.maxSize) {
      const val = this.set.shift()
      if (val) this.onRemove(val[1])
    }
  }

  onRemove = (_val: T) => {
    // Custom logic for when an item is removed
  }
}
